import React from 'react';
import { ArrowLeft, ExternalLink } from 'lucide-react';

const BRAND_BLUE = '#2C73EB';
const FUTURA =
  'Futura, -apple-system, BlinkMacSystemFont, sans-serif';

const goHome = () => {
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};

// Each step is paired with a persona portrait whose emotional state
// thematically matches that stage of the decision. Colors match the
// ovals in DEF's "Elements of a Good Decision" diagram.
const chainSteps = [
  {
    id: 'frame',
    number: '01',
    title: 'Helpful Frame',
    color: '#E87722',
    tintBg: 'rgba(232, 119, 34, 0.08)',
    summary:
      'Clearly define what decision is actually being made, and what is not.',
    detail:
      "A good frame names the real decision, separates it from symptoms, and surfaces the perspective being used. Coaches help clients move from \"I'm stuck\" to \"Here is the specific choice I'm facing, and here is how I'm looking at it.\"",
    coachingMove:
      'Ask: "What exactly are you deciding? What would and wouldn\'t change depending on your choice?"',
    persona: {
      src: '/images/DC Images/Andres/Andres_Overwhelmed.png',
      name: 'Andres',
      emotion: 'Overwhelmed',
      caption:
        '"I\'m stuck. I don\'t even know what I\'m really trying to decide."',
      flip: true,
    },
  },
  {
    id: 'values',
    number: '02',
    title: 'Clear Values',
    color: '#6BBE3E',
    tintBg: 'rgba(107, 190, 62, 0.1)',
    summary:
      'Name what truly matters in this decision, and in what priority.',
    detail:
      'Values are the criteria a good option must satisfy. Without clear values, any option can look reasonable. Coaches help clients articulate and rank what matters, not just surface preferences.',
    coachingMove:
      'Ask: "What has to be true for you to feel good about this decision a year from now?"',
    persona: {
      src: '/images/DC Images/Kavya/Kavya_Visioning.png',
      name: 'Kavya',
      emotion: 'Visioning',
      caption:
        '"What I actually want is to feel proud of how I spend my time."',
    },
  },
  {
    id: 'alternatives',
    number: '03',
    title: 'Creative Alternatives',
    color: '#F5B223',
    tintBg: 'rgba(245, 178, 35, 0.1)',
    summary: 'Generate a real set of options, not a forced binary.',
    detail:
      '"Should I do X or not?" is rarely the right question. Strong decisions come from a meaningful set of distinct alternatives, including hybrid options the client has not considered.',
    coachingMove:
      'Ask: "What are three more options you haven\'t taken seriously yet? What would a trusted mentor suggest?"',
    persona: {
      src: '/images/DC Images/Persona4/Persona4_Exploring.png',
      name: 'Alex',
      emotion: 'Exploring',
      caption:
        '"Wait... what if I don\'t have to commit to a path I can\'t picture yet?"',
    },
  },
  {
    id: 'information',
    number: '04',
    title: 'Useful Information',
    color: '#D7342A',
    tintBg: 'rgba(215, 52, 42, 0.08)',
    summary:
      'Gather the information that would actually change the decision.',
    detail:
      'Not all information is useful. What matters is the information that would cause you to choose differently. Coaches help clients separate assumptions from facts and identify what is worth the effort to learn.',
    coachingMove:
      'Ask: "What could you learn that would actually change your answer? Who would you need to talk to?"',
    persona: {
      src: '/images/DC Images/Andres/Andres_Curious.png',
      name: 'Andres',
      emotion: 'Curious',
      caption:
        '"Who should I actually be talking to before I decide this?"',
    },
  },
  {
    id: 'reasoning',
    number: '05',
    title: 'Sound Reasoning',
    color: '#8E3C9B',
    tintBg: 'rgba(142, 60, 155, 0.08)',
    summary:
      'Weigh alternatives against values, honestly, including trade-offs and uncertainty.',
    detail:
      "Sound reasoning evaluates each option against the client's stated values, considers likely outcomes, and surfaces biases. It invites the client to think probabilistically and to test assumptions.",
    coachingMove:
      'Ask: "If this option goes well, what does that look like? If it goes badly, what did we miss?"',
    persona: {
      src: '/images/DC Images/Persona4/Persona4_Visioning.png',
      name: 'Alex',
      emotion: 'Visioning',
      caption:
        '"If I trace this all the way through, which path can I actually commit to?"',
    },
  },
  {
    id: 'commitment',
    number: '06',
    title: 'Commitment to Follow Through',
    color: '#229FD0',
    tintBg: 'rgba(34, 159, 208, 0.1)',
    summary: 'Turn the decision into concrete, accountable action.',
    detail:
      'A decision is only real when it becomes action. Coaches help clients commit to specific next steps, define how they will know it is working, and plan for setbacks.',
    coachingMove:
      'Ask: "What is the first thing you will do, by when, and how will you know if it\'s working?"',
    persona: {
      src: '/images/DC Images/Persona5/Persona5_Experimenting.png',
      name: 'Sarah',
      emotion: 'Experimenting',
      caption:
        '"Okay, here\'s exactly what I\'m going to try first, and when."',
    },
  },
];

const dqDimensions = [
  {
    name: 'Framing',
    description:
      'How clearly the decision is defined, scoped, and understood.',
  },
  {
    name: 'Values',
    description:
      "How well the client's priorities and criteria are articulated and ranked.",
  },
  {
    name: 'Alternatives',
    description:
      'The range and creativity of options being seriously considered.',
  },
  {
    name: 'Information',
    description:
      'The quality and relevance of information brought to bear on the choice.',
  },
  {
    name: 'Reasoning',
    description:
      'How rigorously options are evaluated against values and uncertainty.',
  },
  {
    name: 'Commitment',
    description:
      'How concretely the decision is translated into action and accountability.',
  },
];

// Portraits used in the "Meet the clients you'll coach" gallery
const personaGallery = [
  {
    name: 'Jamie',
    desc: 'Sophomore engineering student considering switching to art & design.',
    src: '/images/DC Images/Jamie/Jamie_LandingPage.png',
  },
  {
    name: 'Andres',
    desc: 'Software engineer weighing a career pivot away from coding.',
    src: '/images/DC Images/Andres/Andres_LandingPage.png',
    flip: true,
  },
  {
    name: 'Kavya',
    desc: 'Recent graduate exploring what kind of career actually fits her.',
    src: '/images/DC Images/Kavya/Kavya_LandingPage.png',
  },
  {
    name: 'Alex',
    desc: 'High school senior with strong academics but no clear career direction, facing application deadlines.',
    src: '/images/DC Images/Persona4/Persona4_LandingPage.png',
  },
  {
    name: 'Sarah',
    desc: 'Professional planning her return to work after a career break.',
    src: '/images/DC Images/Persona5/Persona5_LandingPage.png',
  },
];

const AboutPage = () => {
  return (
    <div
      className="min-h-screen bg-white text-gray-900 flex flex-col relative"
      style={{ animation: 'pageFadeIn 0.6s ease-out' }}
    >
      <style>{`
        @keyframes pageFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes floatIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .about-float-in { animation: floatIn 0.6s ease-out both; }
      `}</style>

      {/* Header - mirrors landing/home header */}
      <div className="px-6 py-4 relative z-20 sm:px-8 sm:py-8 flex items-start justify-between">
        <div
          className="text-black font-bold text-[25px] leading-[28px] sm:text-2xl cursor-pointer select-none"
          onClick={goHome}
        >
          <div>Decision</div>
          <div>Coach</div>
        </div>
        <button
          type="button"
          onClick={goHome}
          className="mt-1 flex items-center gap-2 text-sm text-gray-600 hover:text-black transition-colors sm:text-base"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>
      </div>

      {/* Hero */}
      <section className="px-6 pt-4 pb-10 sm:px-8 sm:pt-6 sm:pb-16 max-w-6xl mx-auto w-full">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          <div className="about-float-in">
            <div
              className="text-xs font-semibold tracking-[0.2em] uppercase mb-4"
              style={{ color: BRAND_BLUE }}
            >
              The science behind Decision Coach
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl text-black leading-[1.05] mb-5"
              style={{ fontFamily: FUTURA, fontWeight: 400 }}
            >
              A framework for better decisions.
            </h1>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl">
              Decision Coach is built on the{' '}
              <strong className="text-gray-900 font-semibold">
                Decision Chain
              </strong>
              , the research-based framework developed by the{' '}
              <a
                href="https://www.decisioneducation.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:underline underline-offset-4"
                style={{ color: BRAND_BLUE }}
              >
                Decision Education Foundation
              </a>{' '}
              to teach young people how to make better life decisions. Every
              coaching conversation is structured, scored, and coached
              against this framework.
            </p>
          </div>

          {/* Centerpiece diagram */}
          <div
            className="about-float-in"
            style={{ animationDelay: '0.1s' }}
          >
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-[3rem] blur-2xl opacity-50"
                style={{
                  background:
                    'radial-gradient(circle at center, rgba(44,115,235,0.15), transparent 70%)',
                }}
                aria-hidden="true"
              />
              <img
                src="/images/decisionchain.png"
                alt="The Decision Chain: Helpful Frame, Clear Values, Creative Alternatives, Useful Information, Sound Reasoning, and Commitment to Follow Through"
                className="relative w-full max-w-xl mx-auto"
              />
            </div>
            <p className="mt-4 text-center text-xs text-gray-500 italic">
              The Elements of a Good Decision, as taught by the Decision
              Education Foundation.
            </p>
          </div>
        </div>
      </section>

      {/* About DEF */}
      <section className="px-6 sm:px-8 max-w-6xl mx-auto w-full mb-16 sm:mb-24">
        <div className="border-t border-gray-100 pt-12 sm:pt-16">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-14">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-gray-500">
                Framework credit
              </div>
              <h2
                className="text-2xl sm:text-3xl text-black leading-tight"
                style={{ fontFamily: FUTURA, fontWeight: 400 }}
              >
                Grounded in the Decision Chain framework
              </h2>
            </div>
            <div className="text-gray-700 leading-relaxed space-y-4 text-base sm:text-lg">
              <p>
                Decision Coach is built around the{' '}
                <strong className="text-gray-900 font-semibold">
                  Decision Chain
                </strong>
                : a six-link framework that describes what makes any decision
                a <em>high-quality</em> decision, regardless of how it turns
                out. We use this framework to structure coaching conversations
                and to score how well a coach guides their client through a
                real decision.
              </p>
              <p>
                The Decision Chain was developed by the{' '}
                <strong className="text-gray-900 font-semibold">
                  Decision Education Foundation
                </strong>
                {' '}(DEF), a nonprofit that translates decades of academic
                research in decision analysis (the tradition pioneered at
                Stanford and SRI International) into curricula that educators
                use in classrooms across the country.
              </p>
              <a
                href="https://www.decisioneducation.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium hover:underline underline-offset-4 pt-2"
                style={{ color: BRAND_BLUE }}
              >
                Learn more at decisioneducation.org
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Chain Steps */}
      <section className="px-6 sm:px-8 max-w-6xl mx-auto w-full mb-16 sm:mb-24">
        <div className="border-t border-gray-100 pt-12 sm:pt-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-gray-500">
              The framework
            </div>
            <h2
              className="text-3xl sm:text-4xl text-black leading-tight mb-4"
              style={{ fontFamily: FUTURA, fontWeight: 400 }}
            >
              The six elements of a good decision
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              A decision is only as strong as its weakest link. Together,
              these six elements make a decision high-quality, whether
              you're choosing a major, a job, or how to handle a hard
              conversation. Each element shows up as a different moment in
              a coaching conversation.
            </p>
          </div>

          <ol className="space-y-10 sm:space-y-12">
            {chainSteps.map((step) => (
              <li
                key={step.id}
                className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-8 items-start"
              >
                {/* Badge */}
                <div className="flex sm:flex-col items-center sm:items-start">
                  <span
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-semibold flex-shrink-0"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <div className="min-w-0">
                  <h3
                    className="text-xl sm:text-2xl text-black mb-2"
                    style={{ fontFamily: FUTURA, fontWeight: 400 }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-gray-800 font-medium leading-relaxed mb-2">
                    {step.summary}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {step.detail}
                  </p>
                  <div
                    className="inline-block max-w-full rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3"
                    style={{ borderLeft: `3px solid ${step.color}` }}
                  >
                    <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-500 mb-1">
                      Coaching move
                    </div>
                    <p className="text-sm text-gray-800 italic leading-relaxed">
                      {step.coachingMove}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Meet the clients you'll coach */}
      <section className="px-6 sm:px-8 max-w-6xl mx-auto w-full mb-16 sm:mb-24">
        <div className="border-t border-gray-100 pt-12 sm:pt-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-gray-500">
              Who you'll coach
            </div>
            <h2
              className="text-3xl sm:text-4xl text-black leading-tight mb-4"
              style={{ fontFamily: FUTURA, fontWeight: 400 }}
            >
              Clients that feel real.
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              Every persona in Decision Coach brings a distinct decision,
              emotional state, and readiness to engage, so you practice
              coaching across the full range of real human situations, not
              just easy ones.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {personaGallery.map((p) => (
              <div
                key={p.name}
                className="text-center group"
              >
                <div
                  className="mx-auto w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-[1.05]"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className="w-full h-full object-cover rounded-full"
                    style={{
                      opacity: 0.9,
                      ...(p.flip ? { transform: 'scaleX(-1)' } : {}),
                    }}
                  />
                </div>
                <div
                  className="text-sm font-semibold text-gray-900"
                  style={{ fontFamily: FUTURA }}
                >
                  {p.name}
                </div>
                <p className="text-xs text-gray-500 leading-snug mt-1 px-1">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Six Dimensions of Decision Quality */}
      <section className="px-6 sm:px-8 max-w-6xl mx-auto w-full mb-16 sm:mb-24">
        <div className="border-t border-gray-100 pt-12 sm:pt-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-gray-500">
              How we score coaching
            </div>
            <h2
              className="text-3xl sm:text-4xl text-black leading-tight mb-4"
              style={{ fontFamily: FUTURA, fontWeight: 400 }}
            >
              The Six Dimensions of Decision Quality
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              The Decision Chain describes the <em>process</em> of a good
              decision. The Six Dimensions of Decision Quality describe what
              gets measurably stronger in the client's thinking as a result
              of good coaching. Decision Coach scores every session along
              these six dimensions in real time.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {dqDimensions.map((d, i) => (
              <div
                key={d.name}
                className="rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div
                  className="text-xs font-semibold tracking-[0.2em] uppercase mb-2"
                  style={{ color: BRAND_BLUE }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3
                  className="text-lg text-black mb-2"
                  style={{ fontFamily: FUTURA, fontWeight: 400 }}
                >
                  {d.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {d.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Decision Coach uses the framework */}
      <section className="px-6 sm:px-8 max-w-6xl mx-auto w-full mb-16 sm:mb-24">
        <div className="border-t border-gray-100 pt-12 sm:pt-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-gray-500">
              In practice
            </div>
            <h2
              className="text-3xl sm:text-4xl text-black leading-tight mb-4"
              style={{ fontFamily: FUTURA, fontWeight: 400 }}
            >
              How Decision Coach applies the framework
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              Decision Coach is not an advice generator. It is a simulated
              coaching environment where you practice guiding a client
              through the Decision Chain, and get measurable feedback on
              how well you did it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
            {[
              {
                tag: 'Chain-aware scoring',
                title: 'Feedback tied to the six links',
                body: 'Every coaching turn is evaluated against the Decision Chain and the Six Dimensions of Decision Quality. You see which link is strongest, which is weakest, and what to try next.',
              },
              {
                tag: 'Deliberate practice',
                title: 'Coaching as a skill you can train',
                body: 'Decision coaching is a learnable skill. Decision Coach turns it into deliberate practice: repeatable reps, immediate feedback, and a clear model of what "good" looks like.',
              },
            ].map((card) => (
              <div
                key={card.tag}
                className="rounded-2xl border border-gray-100 bg-white p-6 hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div
                  className="text-xs font-semibold tracking-[0.2em] uppercase mb-2"
                  style={{ color: BRAND_BLUE }}
                >
                  {card.tag}
                </div>
                <h3
                  className="text-lg text-black mb-2"
                  style={{ fontFamily: FUTURA, fontWeight: 400 }}
                >
                  {card.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-16 sm:mt-24">
        <div className="px-6 sm:px-8 max-w-6xl mx-auto w-full">
          <div className="border-t border-gray-100 py-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div
              className="text-black font-bold text-[25px] leading-[28px] sm:text-2xl cursor-pointer select-none"
              onClick={goHome}
            >
              <div>Decision</div>
              <div>Coach</div>
            </div>

            <div className="text-xs sm:text-sm text-gray-500 leading-relaxed sm:text-right">
              <div>
                Built on the{' '}
                <a
                  href="https://www.decisioneducation.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline underline-offset-4"
                  style={{ color: BRAND_BLUE }}
                >
                  Decision Chain framework
                </a>
                .
              </div>
              <div className="mt-1 text-gray-400">
                &copy; {new Date().getFullYear()} Decision Coach
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
